import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

/**
 * Minimal Professional Theme
 * Ultra-clean, perfect spacing, premium feel without clutter
 */
const MinimalProfessionalTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#1F2937';
  const fontFamily = settings.typography?.font_family || 'SF Pro Display, -apple-system, sans-serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    if (num === 0) return 'Zero Rupees Only';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template minimal-professional" style={{ fontFamily, padding: '50px', maxWidth: '210mm', margin: '0 auto', background: '#fff' }}>
      {/* Ultra-clean Header */}
      <div style={{ marginBottom: '60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '30px', borderBottom: `1px solid ${primaryColor}` }}>
          <div>
            {settings.branding?.logo_url && (
              <img src={settings.branding.logo_url} alt="Logo" style={{ maxHeight: '70px', marginBottom: '20px' }} />
            )}
            <h1 style={{ fontSize: '28px', fontWeight: '300', color: primaryColor, margin: 0, letterSpacing: '-0.5px' }}>
              {settings.branding?.company_name || 'COMPANY NAME'}
            </h1>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '48px', fontWeight: '200', color: primaryColor, lineHeight: 1, marginBottom: '8px', letterSpacing: '-1px' }}>QUOTATION</div>
            <div style={{ fontSize: '20px', fontWeight: '400', color: '#6B7280', letterSpacing: '2px' }}>{document.quotation_no || 'QT-001'}</div>
          </div>
        </div>
      </div>

      {/* Minimal Info Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '60px' }}>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF', marginBottom: '15px' }}>CLIENT</div>
          <div style={{ fontSize: '18px', fontWeight: '500', color: primaryColor, marginBottom: '10px' }}>{document.party?.name || 'Customer Name'}</div>
          {document.party?.phone && <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '6px' }}>{document.party.phone}</div>}
          {document.party?.billing_address && (
            <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.8', marginTop: '10px' }}>
              {document.party.billing_address.line1}<br />
              {document.party.billing_address.city}, {document.party.billing_address.state} {document.party.billing_address.pincode}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF', marginBottom: '15px' }}>DETAILS</div>
          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '2' }}>
            <div><span style={{ display: 'inline-block', width: '120px', color: '#9CA3AF' }}>Date:</span> {new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN')}</div>
            {document.meta?.valid_days && (
              <div><span style={{ display: 'inline-block', width: '120px', color: '#9CA3AF' }}>Valid Until:</span> {new Date(Date.now() + document.meta.valid_days * 86400000).toLocaleDateString('en-IN')}</div>
            )}
            {document.meta?.reference_no && (
              <div><span style={{ display: 'inline-block', width: '120px', color: '#9CA3AF' }}>Reference:</span> {document.meta.reference_no}</div>
            )}
          </div>
        </div>
      </div>

      {/* Minimal Items Table */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '50px' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${primaryColor}` }}>
            <th style={{ padding: '15px 0', textAlign: 'left', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF' }}>ITEM</th>
            {settings.toggles?.show_hsn && <th style={{ padding: '15px 0', textAlign: 'center', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF' }}>HSN</th>}
            <th style={{ padding: '15px 0', textAlign: 'right', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF' }}>QTY</th>
            <th style={{ padding: '15px 0', textAlign: 'right', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF' }}>RATE</th>
            <th style={{ padding: '15px 0', textAlign: 'right', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF' }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {(document.items || []).map((item, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #F3F4F6' }}>
              <td style={{ padding: '20px 0', fontSize: '14px', color: primaryColor, fontWeight: '400' }}>
                {item.name}
                {item.description && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px', fontWeight: '300' }}>{item.description}</div>}
              </td>
              {settings.toggles?.show_hsn && <td style={{ padding: '20px 0', textAlign: 'center', fontSize: '12px', color: '#6B7280' }}>{item.hsn || '—'}</td>}
              <td style={{ padding: '20px 0', textAlign: 'right', fontSize: '12px', color: '#6B7280' }}>{item.quantity} {item.unit || 'PCS'}</td>
              <td style={{ padding: '20px 0', textAlign: 'right', fontSize: '12px', color: '#6B7280' }}>{formatCurrency(item.unit_price)}</td>
              <td style={{ padding: '20px 0', textAlign: 'right', fontSize: '14px', color: primaryColor, fontWeight: '500' }}>{formatCurrency(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Minimal Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
        <div style={{ width: '400px' }}>
          <div style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px' }}>
              <span style={{ color: '#6B7280', fontWeight: '400' }}>Subtotal</span>
              <span style={{ fontWeight: '400', color: primaryColor }}>{formatCurrency(document.totals?.subtotal || 0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px' }}>
              <span style={{ color: '#6B7280', fontWeight: '400' }}>Discount</span>
              <span style={{ fontWeight: '400', color: '#DC2626' }}>- {formatCurrency(document.totals?.discount_total || 0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px', paddingTop: '10px', borderTop: '1px solid #E5E7EB' }}>
              <span style={{ color: '#6B7280', fontWeight: '500' }}>Taxable Amount</span>
              <span style={{ fontWeight: '500', color: primaryColor }}>{formatCurrency(document.totals?.taxable_amount || 0)}</span>
            </div>
            {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
              <React.Fragment key={idx}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', paddingLeft: '10px' }}>
                  <span style={{ color: '#6B7280' }}>CGST @ {tax.rate / 2}%</span>
                  <span style={{ fontWeight: '400', color: primaryColor }}>{formatCurrency(tax.amount / 2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', paddingLeft: '10px' }}>
                  <span style={{ color: '#6B7280' }}>SGST @ {tax.rate / 2}%</span>
                  <span style={{ fontWeight: '400', color: primaryColor }}>{formatCurrency(tax.amount / 2)}</span>
                </div>
              </React.Fragment>
            ))}
            {document.totals?.round_off !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px' }}>
                <span style={{ color: '#6B7280' }}>Round Off</span>
                <span style={{ fontWeight: '400', color: primaryColor }}>{document.totals.round_off > 0 ? '+' : ''} {formatCurrency(Math.abs(document.totals.round_off))}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: document.payment?.received_amount ? '15px' : '0' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#6B7280', letterSpacing: '1px' }}>TOTAL</span>
            <span style={{ fontSize: '32px', fontWeight: '300', color: primaryColor, letterSpacing: '-1px' }}>{formatCurrency(document.totals?.grand_total || 0)}</span>
          </div>
          {document.payment?.received_amount && (
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280' }}>Received:</span>
                <span style={{ fontWeight: '500', color: primaryColor }}>{formatCurrency(document.payment.received_amount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: '#6B7280' }}>Balance:</span>
                <span style={{ fontWeight: '500', color: primaryColor }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Amount in Words */}
      <div style={{ background: '#F9FAFB', border: `1px solid ${primaryColor}`, borderRadius: '6px', padding: '15px', marginBottom: '40px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', color: primaryColor, marginBottom: '6px', letterSpacing: '2px' }}>AMOUNT IN WORDS</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', textTransform: 'uppercase' }}>
          {numberToWords(document.totals?.grand_total || 0)}
        </div>
      </div>

      {/* Minimal Footer */}
      {(document.terms?.notes_public || settings.toggles?.show_signature) && (
        <div style={{ paddingTop: '40px', borderTop: '1px solid #E5E7EB' }}>
          {document.terms?.notes_public && (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#9CA3AF', marginBottom: '10px' }}>NOTES</div>
              <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.8', maxWidth: '600px' }}>
                {document.terms.notes_public}
              </div>
            </div>
          )}
          {settings.toggles?.show_signature && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
              <div>
                {settings.qr_code_data && <QRCode value={settings.qr_code_data} size={70} />}
              </div>
              <div style={{ textAlign: 'center', minWidth: '200px' }}>
                <div style={{ borderTop: `1px solid ${primaryColor}`, paddingTop: '10px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '500', color: '#9CA3AF', letterSpacing: '2px' }}>AUTHORIZED BY</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MinimalProfessionalTheme;